import { useEffect, useState } from 'react'

import { Search } from '@mui/icons-material'
import { Box, InputAdornment, TextField, Paper, Typography, CircularProgress } from '@mui/material'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useDebounce } from 'use-debounce'

import { DEBOUNCE_DELAY } from 'constants/index'
import { useGetBookingInvoiceBatchesLazyQuery } from 'generated/graphql'
import { invoiceBatchFiltersFormVar } from 'graphql/reactiveVariables'

const BatchSearchAutocomplete = () => {
  const { setValue, handleSubmit, control } = useFormContext<any>()
  const [showSearchResult, setShowSearchResult] = useState<boolean>(false)
  
  const inputValue = useWatch({ name: 'batchSearch' })
  const [debouncedInputValue] = useDebounce(inputValue, DEBOUNCE_DELAY)

  const [getBatches, { data, loading }] = useGetBookingInvoiceBatchesLazyQuery({
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (debouncedInputValue && debouncedInputValue.length >= 1) {
      const batchId = parseInt(debouncedInputValue, 10)
      if (!isNaN(batchId)) {
        getBatches({
          variables: {
            where: {
              id: { eq: batchId }
            }
          }
        })
      }
    }
  }, [debouncedInputValue, getBatches])

  const onSubmit = (data: any) => {
    invoiceBatchFiltersFormVar(data)    
  }

  const handleBatchClick = (batchId: string) => {
    setValue('batchSearch', batchId)
    handleSubmit(onSubmit)()
    setShowSearchResult(false)
  }

  const handleSearchFocus = () => {
    setShowSearchResult(true)
  }

  const handleSearchBlur = () => {
    setTimeout(() => {
      setShowSearchResult(false)
    }, 200)
  }

  const batches = data?.bookingInvoiceBatches?.edges || []
  const hasResults = batches.length > 0
  const showResults = inputValue && showSearchResult && (hasResults || loading)

  return (
    <Box position='relative'>
      <Controller
        control={control}
        name='batchSearch'
        render={({ field: { ref, ...restField } }) => (
          <TextField
            {...restField}
            inputRef={ref}
            fullWidth
            placeholder='Batch ID'
            inputProps={{ 'aria-label': 'search batches' }}
            value={inputValue || ''}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      {showResults && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            maxHeight: '200px',
            overflow: 'auto',
            mt: 1
          }}
        >
          {loading && (
            <Box p={2} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Searching...
              </Typography>
            </Box>
          )}
          
          {!loading && hasResults && (
            <>
              {batches.map((batch) => (
                <Box
                  key={batch.node.id}
                  p={1}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => handleBatchClick(batch.node.id)}
                >
                  <Typography variant="body2">
                    <strong>Batch ID:</strong> {batch.node.id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Date: {new Date(batch.node.date).toLocaleDateString()} | 
                    Currency: {batch.node.currency?.code}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          
          {!loading && !hasResults && (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                No results found
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  )
}

export default BatchSearchAutocomplete